import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Building, Users, AlertTriangle, Clock, PlayCircle, Plus, X } from 'lucide-react';
import { sponsors, vevoSweep } from '@/lib/api';

export default function SponsorsPage() {
  const [list, setList] = React.useState([]);
  const [oblig, setOblig] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [selectedDetail, setSelectedDetail] = React.useState(null);
  const [showNew, setShowNew] = React.useState(false);
  const [busyBatch, setBusyBatch] = React.useState(false);
  const [lastBatchResult, setLastBatchResult] = React.useState(null);
  const [sweepStatus, setSweepStatus] = React.useState(null);
  const [sweeping, setSweeping] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([sponsors.list(), sponsors.obligations(), vevoSweep.status()])
      .then(([l, o, s]) => { setList(l); setOblig(o); setSweepStatus(s); })
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(load, [load]);

  const openSponsor = async (sid) => {
    setSelected(sid);
    setSelectedDetail(null);
    const d = await sponsors.get(sid);
    setSelectedDetail(d);
  };

  const runBatch = async () => {
    if (!selected) return;
    setBusyBatch(true); setLastBatchResult(null);
    try {
      const r = await sponsors.batchVevo(selected);
      setLastBatchResult(r);
      // Refresh detail
      const d = await sponsors.get(selected);
      setSelectedDetail(d);
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Batch VEVO failed');
    } finally { setBusyBatch(false); }
  };

  return (
    <div className="mx-auto max-w-7xl p-8" data-testid="sponsors-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building className="h-6 w-6 text-[#2ECC9A]" />
          <h1 className="text-2xl font-serif font-bold text-[#1A3A5C]">Sponsor obligations</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="sweep-run" variant="outline"
                  onClick={async () => {
                    setSweeping(true);
                    try {
                      const r = await vevoSweep.runNow();
                      window.alert(`Sweep complete — checked ${r.checked}, expiring ${r.expiring}`);
                      load();
                    } catch (e) {
                      window.alert(e?.response?.data?.detail || 'Sweep failed');
                    } finally { setSweeping(false); }
                  }}
                  disabled={sweeping}
                  className="h-9 gap-1 border-[#2ECC9A]/40 bg-[#2ECC9A]/5 text-[#0F766E] text-xs">
            {sweeping ? <Loader2 className="h-3 w-3 animate-spin"/> : <><PlayCircle className="h-3 w-3"/> Run VEVO sweep</>}
          </Button>
          <Button data-testid="sponsor-new" onClick={() => setShowNew(true)}
                  className="h-9 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            <Plus className="h-3 w-3" /> Add sponsor
          </Button>
        </div>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Central roster of your 482 sponsors, batch VEVO checks, and obligation alerts
        (expiring visas, sponsorship renewals, LMT windows).
        {sweepStatus?.last_sweep_at && (
          <span className="ml-2 text-slate-400">
            · Last swept {new Date(sweepStatus.last_sweep_at).toLocaleDateString('en-AU')}
            {sweepStatus.last_expiring_count > 0 && ` — ${sweepStatus.last_expiring_count} expiring flagged`}
          </span>
        )}
      </p>

      {/* Obligation summary */}
      {oblig && (
        <div className="mt-4 grid gap-3 sm:grid-cols-4">
          <ObligKpi icon={Users} label="Sponsors" value={oblig.sponsors_count} tone="slate" testid="oblig-sponsors" />
          <ObligKpi icon={Users} label="Sponsored workers" value={oblig.workers_count} tone="slate" testid="oblig-workers" />
          <ObligKpi icon={AlertTriangle} label="Visas expiring <90d"
                    value={oblig.visas_expiring_90d.length} tone={oblig.visas_expiring_90d.length ? 'amber' : 'slate'}
                    testid="oblig-expiring" />
          <ObligKpi icon={Clock} label="Stale VEVO checks"
                    value={oblig.vevo_stale.length} tone={oblig.vevo_stale.length ? 'red' : 'slate'}
                    testid="oblig-stale" />
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-xs font-semibold text-[#1A3A5C] mb-3">Roster</div>
            {loading && <div className="text-xs text-slate-400"><Loader2 className="inline h-3 w-3 animate-spin"/> Loading…</div>}
            {!loading && list.length === 0 && (
              <div className="rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
                No sponsors yet. Add one to get started.
              </div>
            )}
            <div className="space-y-1">
              {list.map((s) => (
                <button key={s.id} data-testid={`sponsor-${s.id}`}
                        onClick={() => openSponsor(s.id)}
                        className={`w-full rounded-md border p-3 text-left transition ${
                          selected === s.id ? 'border-[#2ECC9A] bg-[#2ECC9A]/5'
                                             : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}>
                  <div className="text-xs font-semibold text-[#1A3A5C]">{s.name}</div>
                  <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
                    <span>{s.workers_count || 0} worker{s.workers_count === 1 ? '' : 's'}</span>
                    {s.sponsor_type && s.sponsor_type !== 'standard' &&
                      <Badge variant="outline" className="border-slate-200 text-[9px]">{s.sponsor_type}</Badge>}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <div>
          {!selected && (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center text-sm text-slate-400">
                Select a sponsor on the left to see their workers, run a batch VEVO check, and manage obligations.
              </CardContent>
            </Card>
          )}
          {selected && !selectedDetail && (
            <Card><CardContent className="p-8 text-center"><Loader2 className="inline h-4 w-4 animate-spin text-slate-400"/></CardContent></Card>
          )}
          {selectedDetail && (
            <div className="space-y-4">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold text-[#1A3A5C]">{selectedDetail.name}</div>
                      <div className="text-xs text-slate-500 mt-1 space-x-3">
                        {selectedDetail.abn && <span>ABN {selectedDetail.abn}</span>}
                        {selectedDetail.industry && <span>{selectedDetail.industry}</span>}
                        {selectedDetail.sponsor_type &&
                          <Badge variant="outline" className="border-slate-200 text-[10px]">{selectedDetail.sponsor_type}</Badge>}
                      </div>
                      {selectedDetail.sponsorship_expiry && (
                        <div className="mt-2 text-[11px] text-slate-600">
                          Sponsorship expiry: <span className="font-medium">{selectedDetail.sponsorship_expiry}</span>
                        </div>
                      )}
                    </div>
                    <Button data-testid="batch-vevo-run" onClick={runBatch} disabled={busyBatch}
                            className="h-9 gap-1 bg-[#2ECC9A] text-[#0F1F3A] hover:bg-[#2ECC9A]/90 text-xs">
                      {busyBatch ? <Loader2 className="h-3 w-3 animate-spin"/> : <><PlayCircle className="h-3 w-3"/> Batch VEVO</>}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {lastBatchResult && (
                <Card className="border-emerald-200 bg-emerald-50" data-testid="batch-result-card">
                  <CardContent className="p-3 text-xs text-emerald-900">
                    Batch complete — ran {lastBatchResult.ran} check{lastBatchResult.ran === 1 ? '' : 's'}
                    {lastBatchResult.ran_at ? ` at ${new Date(lastBatchResult.ran_at).toLocaleTimeString('en-AU')}` : ''}
                    {lastBatchResult.message ? ` — ${lastBatchResult.message}` : '.'}
                  </CardContent>
                </Card>
              )}

              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-semibold text-[#1A3A5C]">Sponsored workers</div>
                    <Badge variant="outline" className="border-slate-200 text-[10px]">{selectedDetail.workers?.length || 0}</Badge>
                  </div>
                  {selectedDetail.workers?.length === 0 && (
                    <div className="rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
                      Link matters to this sponsor from the matter detail page (set <span className="font-mono">sponsor_id</span>) to see them here.
                    </div>
                  )}
                  <div className="space-y-2">
                    {(selectedDetail.workers || []).map((w) => (
                      <div key={w.id || w.ref} data-testid={`worker-${w.ref}`}
                           className="rounded-md border border-slate-200 p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs font-semibold text-[#1A3A5C]">
                              {w.applicant_name || w.applicant} · <span className="font-mono">{w.ref}</span>
                            </div>
                            <div className="mt-1 text-[10px] text-slate-500">
                              Subclass {w.subclass || w.visa_type || '—'} · {w.status || 'active'}
                            </div>
                          </div>
                          {w.latest_vevo && (
                            <div className="text-right">
                              <div className="text-[10px] text-slate-500">VEVO checked</div>
                              <div className="text-xs font-medium text-[#1A3A5C]">
                                {w.latest_vevo.result?.visa_class || '—'}
                              </div>
                              <div className="text-[10px] text-slate-500">
                                exp {w.latest_vevo.result?.expiry_date || '—'}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {showNew && (
        <SponsorNewDialog onClose={() => setShowNew(false)} onSaved={(s) => {
          setShowNew(false); load(); openSponsor(s.id);
        }} />
      )}
    </div>
  );
}

function ObligKpi({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    slate: 'text-slate-700 bg-slate-50 border-slate-200',
    amber: 'text-amber-800 bg-amber-50 border-amber-200',
    red: 'text-red-800 bg-red-50 border-red-200',
  };
  return (
    <div data-testid={testid} className={`rounded-md border p-3 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
}

function SponsorNewDialog({ onClose, onSaved }) {
  const [form, setForm] = React.useState({
    name: '', abn: '', industry: '', contact_name: '', contact_email: '',
    contact_phone: '', sponsor_type: 'standard', sponsorship_expiry: '',
  });
  const [saving, setSaving] = React.useState(false);
  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const clean = { ...form };
      Object.keys(clean).forEach((k) => { if (clean[k] === '') delete clean[k]; });
      const r = await sponsors.upsert(clean);
      onSaved(r);
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-6" onClick={onClose}>
      <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-5" data-testid="sponsor-new-dialog">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-[#1A3A5C]">New sponsor</div>
            <button onClick={onClose}><X className="h-4 w-4 text-slate-400"/></button>
          </div>
          <div className="mt-3 grid gap-2">
            <div><Label className="text-xs">Company name *</Label>
              <Input data-testid="sponsor-form-name" value={form.name} onChange={(e) => patch('name', e.target.value)} className="mt-1 text-xs"/></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">ABN</Label>
                <Input data-testid="sponsor-form-abn" value={form.abn} onChange={(e) => patch('abn', e.target.value)} className="mt-1 font-mono text-xs"/></div>
              <div><Label className="text-xs">Industry</Label>
                <Input value={form.industry} onChange={(e) => patch('industry', e.target.value)} className="mt-1 text-xs"/></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Sponsor type</Label>
                <select value={form.sponsor_type} onChange={(e) => patch('sponsor_type', e.target.value)}
                        className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs">
                  <option value="standard">Standard</option>
                  <option value="accredited">Accredited</option>
                  <option value="labour_agreement">Labour agreement</option>
                </select></div>
              <div><Label className="text-xs">Sponsorship expiry</Label>
                <Input type="date" value={form.sponsorship_expiry} onChange={(e) => patch('sponsorship_expiry', e.target.value)} className="mt-1 text-xs"/></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Contact name</Label>
                <Input value={form.contact_name} onChange={(e) => patch('contact_name', e.target.value)} className="mt-1 text-xs"/></div>
              <div><Label className="text-xs">Contact email</Label>
                <Input value={form.contact_email} onChange={(e) => patch('contact_email', e.target.value)} className="mt-1 text-xs"/></div>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="h-9 text-xs">Cancel</Button>
            <Button data-testid="sponsor-form-save" onClick={submit} disabled={saving || !form.name.trim()}
                    className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
              {saving ? <Loader2 className="h-3 w-3 animate-spin"/> : 'Save sponsor'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
